/**
 * 🆕 [Phase 24] MigrationBTInternesDialog
 * Modale owner-only pour déclencher la migration Phase 23 (BT internes → client user).
 * UX : prévisualisation (dry-run) obligatoire avant le bouton Appliquer.
 */
import { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Wrench, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function MigrationBTInternesDialog({ open, onClose }) {
  const [email, setEmail] = useState('info@peintureresidentielleqc.com');
  const [telephone, setTelephone] = useState('(418) 264-6937');
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const doDryRun = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${api.baseUrl}/admin/migrations/bt-internes/dry-run`, {
        email: email || null,
        telephone: telephone || null,
        client_id: clientId || null,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur dry-run');
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!preview || preview.status !== 'ok') return;
    if (!window.confirm(`Migrer ${preview.bt_total} BT internes vers « ${preview.target?.nom} » ? Cette action archive l'ancien client legacy.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${api.baseUrl}/admin/migrations/bt-internes/apply`, {
        email: email || null,
        telephone: telephone || null,
        client_id: clientId || null,
        confirm: true,
      });
      setResult(data);
      if (data?.validation?.drift) {
        toast.warning(`⚠️ Drift détecté — vérifier le log`);
      } else {
        toast.success(`✅ ${data.bt_moved} BT migrés`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur apply');
    } finally { setBusy(false); }
  };

  const reset = () => { setPreview(null); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="migration-bt-internes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-slate-600" />
            Migrer les BT internes vers ce client
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Cette action déplace tous les bons de travail internes (ancien client legacy)
            vers votre client commercial « Peinture Résidentielle Qc ». Idempotente — peut
            être relancée sans risque.
          </p>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Email du client cible</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="migration-email" />
            </div>
            <div>
              <Label className="text-xs">Téléphone du client cible</Label>
              <Input value={telephone} onChange={(e) => setTelephone(e.target.value)} data-testid="migration-tel" />
            </div>
            <div>
              <Label className="text-xs">Ou ID du client cible (override direct)</Label>
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="(optionnel)" data-testid="migration-client-id" />
            </div>
          </div>
        )}

        {preview && preview.status === 'no_match' && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex gap-2" data-testid="migration-no-match">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Aucun client correspondant aux critères</p>
              <p className="text-xs mt-1">Vérifiez l&apos;email/téléphone ou utilisez l&apos;ID direct.</p>
              {preview.candidates?.length > 0 && (
                <ul className="mt-2 text-xs">
                  {preview.candidates.slice(0, 5).map((c) => (
                    <li key={c.id}>• {c.nom} — tel: {c.tel || '?'} (id: {c.id})</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {preview && preview.status === 'ok' && !result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-2" data-testid="migration-preview">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold">
              <CheckCircle2 className="w-5 h-5" /> Client cible identifié
            </div>
            <p><strong>{preview.target?.nom}</strong> — {preview.target?.email} — {preview.target?.telephone}</p>
            <hr className="border-emerald-200" />
            <p>BT internes à migrer : <strong>{preview.bt_moved}</strong> (sur {preview.bt_total} total, {preview.bt_skipped_already_migrated} déjà migré(s))</p>
            <p>Punches : {preview.punches} | Heures totales : <strong>{preview.heures_totales}h</strong></p>
          </div>
        )}

        {result && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm space-y-1" data-testid="migration-result">
            <p className="font-semibold text-blue-900">✅ Migration appliquée</p>
            <p>{result.bt_moved} BT déplacés vers « {result.target?.nom} »</p>
            {result.validation && (
              <p className={result.validation.drift ? 'text-red-700 font-bold' : 'text-emerald-700'}>
                {result.validation.drift ? '⚠️ DRIFT DÉTECTÉ — vérifier le log' :
                  `✅ Heures préservées : ${result.validation.heures_avant}h avant / ${result.validation.heures_apres}h après`}
              </p>
            )}
            <p className="text-xs text-blue-700">Log markdown disponible dans `/app/memory/`.</p>
          </div>
        )}

        <DialogFooter>
          {!preview ? (
            <>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={doDryRun} disabled={busy} data-testid="migration-btn-preview">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Prévisualiser la migration
              </Button>
            </>
          ) : result ? (
            <Button onClick={() => { reset(); onClose && onClose(); }}>Fermer</Button>
          ) : (
            <>
              <Button variant="outline" onClick={reset}>Modifier les critères</Button>
              {preview.status === 'ok' && (
                <Button onClick={doApply} disabled={busy || preview.bt_moved === 0} data-testid="migration-btn-apply" className="bg-red-600 hover:bg-red-700 text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Appliquer la migration
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
